/**
 * DivingAcademy - Página ultra-visual de cursos de buceo
 * Integra MarketIQ: Google Images, Google Maps, Google News
 * Hero con imágenes rotativas, cards con thumbnails, spots, noticias, galería
 */
import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '../components/ui/Button'
import { cn } from '../lib/cn'
import {
  divingSpecialties,
  getAllTags,
  type DivingSpecialty,
} from '../courses/divingSpecialties'
import { getSpecialtyCards } from '../courses/divingSpecialtiesQuiz'
import {
  certificationLevels,
  getDivingLearningPath,
  catalogStats,
} from '../courses/divingCatalog'
import {
  getCachedImages,
  getCachedMaps,
  getCachedNews,
  type GoogleImage,
  type GoogleMapsPlace,
  type GoogleNewsResult,
} from '../lib/marketiq'

// ═══════════════════════════════════════════════════════════════════
// HOOKS
// ═══════════════════════════════════════════════════════════════════

/** Precarga thumbnails de todas las especialidades de golpe */
function useSpecialtyThumbnails(lang: string) {
  const [thumbs, setThumbs] = useState<Record<string, string>>({})

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const batch: Record<string, string> = {}
      // Lanzar todo en paralelo (el caché evita duplicados)
      await Promise.allSettled(
        divingSpecialties.map(async (s) => {
          const q = lang === 'es' ? s.imageQuery : s.imageQueryEn
          const imgs = await getCachedImages(q, { language: lang })
          if (imgs[0] && !cancelled) {
            batch[s.id] = imgs[0].thumbnail || imgs[0].original
          }
        })
      )
      if (!cancelled) setThumbs(prev => ({ ...prev, ...batch }))
    }
    load()
    return () => { cancelled = true }
  }, [lang])

  return thumbs
}

/** Hero images rotativas */
function useHeroImages(lang: string) {
  const [images, setImages] = useState<GoogleImage[]>([])
  const [current, setCurrent] = useState(0)

  useEffect(() => {
    let cancelled = false
    const q = lang === 'es'
      ? 'mejores fotografías submarinas buceo arrecife coral'
      : 'best underwater photography scuba diving coral reef'
    getCachedImages(q, { language: lang }).then(imgs => {
      if (!cancelled && imgs.length > 0) setImages(imgs.slice(0, 8))
    })
    return () => { cancelled = true }
  }, [lang])

  useEffect(() => {
    if (images.length < 2) return
    const interval = setInterval(() => {
      setCurrent(prev => (prev + 1) % images.length)
    }, 4000)
    return () => clearInterval(interval)
  }, [images.length])

  return { images, current }
}

/** Imágenes para una especialidad seleccionada */
function useSpecialtyImages(specialty: DivingSpecialty | null, lang: string) {
  const [images, setImages] = useState<GoogleImage[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!specialty) { setImages([]); return }
    let cancelled = false
    setLoading(true)
    const query = lang === 'es' ? specialty.imageQuery : specialty.imageQueryEn
    getCachedImages(query, { language: lang }).then(imgs => {
      if (!cancelled) { setImages(imgs.slice(0, 8)); setLoading(false) }
    })
    return () => { cancelled = true }
  }, [specialty?.id, lang])

  return { images, loading }
}

/** Centros de buceo vía Google Maps */
function useDiveSpots() {
  const [spots, setSpots] = useState<GoogleMapsPlace[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    getCachedMaps('best scuba diving center').then(places => {
      if (!cancelled) { setSpots(places.slice(0, 6)); setLoading(false) }
    })
    return () => { cancelled = true }
  }, [])

  return { spots, loading }
}

/** Noticias de buceo vía Google News */
function useDivingNews(lang: string) {
  const [news, setNews] = useState<GoogleNewsResult[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const q = lang === 'es' ? 'noticias buceo submarino' : 'scuba diving news'
    getCachedNews(q, lang).then(items => {
      if (!cancelled) { setNews(items.slice(0, 5)); setLoading(false) }
    })
    return () => { cancelled = true }
  }, [lang])

  return { news, loading }
}

/** Galería featured */
function useFeaturedGallery(lang: string) {
  const [images, setImages] = useState<GoogleImage[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const queries = lang === 'es'
      ? ['fotografia submarina arrecife coral tropicales', 'buceo cueva cenote azul', 'vida marina pelágicos tiburones mantas']
      : ['underwater photography coral reef tropical', 'cave diving cenote blue hole', 'marine life pelagic sharks manta rays']

    Promise.allSettled(
      queries.map(q => getCachedImages(q, { language: lang }))
    ).then(results => {
      if (cancelled) return
      const all: GoogleImage[] = []
      results.forEach(r => { if (r.status === 'fulfilled') all.push(...r.value.slice(0, 4)) })
      setImages(all.slice(0, 12))
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [lang])

  return { images, loading }
}

/** Imágenes para niveles de certificación */
function useCertImages(lang: string) {
  const [imgs, setImgs] = useState<Record<string, string>>({})

  useEffect(() => {
    let cancelled = false
    Promise.allSettled(
      certificationLevels.map(async (level) => {
        const result = await getCachedImages(level.imageQuery, { language: lang })
        if (result[0] && !cancelled) {
          setImgs(prev => ({ ...prev, [level.id]: result[0].thumbnail || result[0].original }))
        }
      })
    )
    return () => { cancelled = true }
  }, [lang])

  return imgs
}

// ═══════════════════════════════════════════════════════════════════
// SMALL COMPONENTS
// ═══════════════════════════════════════════════════════════════════

function DifficultyBadge({ level, t }: { level: number; t: (k: string) => string }) {
  const labels = ['', t('divingAcademy.beginner'), t('divingAcademy.intermediate'), t('divingAcademy.advanced'), t('divingAcademy.expert'), t('divingAcademy.expert')]
  const colors = ['', 'bg-green-100 text-green-700', 'bg-blue-100 text-blue-700', 'bg-orange-100 text-orange-700', 'bg-red-100 text-red-700', 'bg-red-200 text-red-800']
  return (
    <span className={cn('text-xs font-bold px-2 py-0.5 rounded-full', colors[level])}>
      {'★'.repeat(level)} {labels[level]}
    </span>
  )
}

function PrereqBadge({ prereq, t }: { prereq: string; t: (k: string) => string }) {
  const map: Record<string, { label: string; color: string }> = {
    'open-water': { label: t('divingAcademy.openWater'), color: 'bg-cyan-100 text-cyan-700' },
    'advanced-open-water': { label: t('divingAcademy.advancedOpenWater'), color: 'bg-indigo-100 text-indigo-700' },
    'rescue-diver': { label: t('divingAcademy.rescueDiver'), color: 'bg-red-100 text-red-700' },
  }
  const info = map[prereq] ?? { label: prereq, color: 'bg-gray-100 text-gray-600' }
  return (
    <span className={cn('text-xs font-bold px-2 py-0.5 rounded-full', info.color)}>
      {info.label}
    </span>
  )
}

/** Shimmer placeholder for loading images */
function ImageShimmer({ className }: { className?: string }) {
  return (
    <div className={cn('bg-gradient-to-r from-white/10 via-white/20 to-white/10 animate-pulse rounded-2xl', className)} />
  )
}

// ═══════════════════════════════════════════════════════════════════
// HERO BANNER WITH ROTATING IMAGES
// ═══════════════════════════════════════════════════════════════════
function HeroBanner({ lang }: { lang: string }) {
  const { t } = useTranslation()
  const { images, current } = useHeroImages(lang)

  return (
    <div className="relative h-56 sm:h-72 rounded-3xl overflow-hidden mb-6 border-2 border-white/30 shadow-2xl">
      {/* Rotating background images */}
      {images.map((img, i) => (
        <div
          key={i}
          className={cn(
            'absolute inset-0 transition-opacity duration-1000',
            i === current ? 'opacity-100' : 'opacity-0'
          )}
        >
          <img
            src={img.original || img.thumbnail}
            alt={img.title}
            className="w-full h-full object-cover"
            loading={i < 2 ? 'eager' : 'lazy'}
            onError={(e) => { (e.target as HTMLImageElement).src = img.thumbnail }}
          />
        </div>
      ))}

      {/* Fallback gradient when no images yet */}
      {images.length === 0 && (
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-blue-500 to-cyan-400 animate-pulse" />
      )}

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/40 to-transparent" />

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <h1 className="text-3xl sm:text-4xl font-black text-white drop-shadow-2xl flex items-center gap-3">
          <span>🤿</span>
          {t('divingAcademy.title')}
        </h1>
        <p className="text-white/80 font-bold text-sm mt-1 drop-shadow-lg">
          {t('divingAcademy.subtitle')}
        </p>
      </div>

      {/* Image indicator dots */}
      {images.length > 1 && (
        <div className="absolute top-4 right-4 flex gap-1.5">
          {images.slice(0, 6).map((_, i) => (
            <div
              key={i}
              className={cn(
                'w-2 h-2 rounded-full transition-all duration-300',
                i === current % 6 ? 'bg-white scale-125' : 'bg-white/40'
              )}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════
// STATS CARD
// ═══════════════════════════════════════════════════════════════════
function StatsCard() {
  const { t } = useTranslation()
  return (
    <div className="rounded-3xl p-5 border-2 border-white/40 shadow-2xl"
      style={{
        background: 'linear-gradient(135deg, rgba(6,182,212,0.9) 0%, rgba(14,165,233,0.95) 50%, rgba(99,102,241,0.9) 100%)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
      }}
    >
      <div className="grid grid-cols-3 gap-3">
        {[
          { n: catalogStats.totalCourses, label: t('divingAcademy.courses'), emoji: '📚' },
          { n: catalogStats.totalSpecialties, label: t('divingAcademy.specialties'), emoji: '🏅' },
          { n: catalogStats.totalQuestions, label: t('divingAcademy.questions'), emoji: '🧠' },
        ].map(s => (
          <div key={s.label} className="text-center p-3 rounded-2xl bg-white/15">
            <span className="text-xl">{s.emoji}</span>
            <span className="text-2xl font-black text-white block">{s.n}</span>
            <p className="text-white/80 text-[10px] font-bold mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════
// SPECIALTY CARD WITH THUMBNAIL
// ═══════════════════════════════════════════════════════════════════
function SpecialtyCard({
  specialty, lang, isSelected, onSelect, thumbnail,
}: {
  specialty: DivingSpecialty; lang: string; isSelected: boolean; onSelect: () => void; thumbnail?: string
}) {
  const { t } = useTranslation()
  const name = lang === 'es' ? specialty.name : specialty.nameEn
  const desc = lang === 'es' ? specialty.description : specialty.descriptionEn
  const quizCount = getSpecialtyCards(specialty.id).length

  return (
    <button
      onClick={onSelect}
      className={cn(
        'w-full text-left rounded-3xl border-2 overflow-hidden transition-all duration-300',
        'hover:scale-[1.02] hover:shadow-2xl active:scale-[0.98]',
        isSelected
          ? 'border-white/70 shadow-2xl ring-2 ring-white/50'
          : 'border-white/30 shadow-xl hover:border-white/50'
      )}
    >
      {/* Image header */}
      <div className="relative h-32 overflow-hidden">
        {thumbnail ? (
          <img src={thumbnail} alt={name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className={cn('w-full h-full', `bg-gradient-to-br ${specialty.gradient}`)} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute top-3 left-3">
          <span className={cn('text-xs font-bold px-2.5 py-1 rounded-full backdrop-blur-md', 'bg-black/30 text-white border border-white/20')}>
            {specialty.emoji} {name}
          </span>
        </div>
        <div className="absolute top-3 right-3">
          <DifficultyBadge level={specialty.difficulty} t={t} />
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <p className="text-white text-xs font-medium drop-shadow line-clamp-2">{desc}</p>
        </div>
      </div>

      {/* Bottom info */}
      <div className="p-3 bg-white/90 backdrop-blur-sm">
        <div className="flex flex-wrap gap-1.5">
          <PrereqBadge prereq={specialty.prerequisite} t={t} />
          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
            🧠 {quizCount} {t('divingAcademy.questions')}
          </span>
          {specialty.requiredDives > 0 && (
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
              🤿 {specialty.requiredDives} {t('divingAcademy.dives')}
            </span>
          )}
          {specialty.maxDepth && (
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
              ↓ {specialty.maxDepth}m
            </span>
          )}
        </div>
      </div>
    </button>
  )
}

// ═══════════════════════════════════════════════════════════════════
// IMAGE GALLERY (masonry-style)
// ═══════════════════════════════════════════════════════════════════
function ImageGallery({ images, loading }: { images: GoogleImage[]; loading: boolean }) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-2">
        {[...Array(4)].map((_, i) => <ImageShimmer key={i} className={cn('aspect-[4/3]', i === 0 && 'col-span-2 aspect-[16/9]')} />)}
      </div>
    )
  }
  if (images.length === 0) return null
  return (
    <div className="grid grid-cols-2 gap-2">
      {images.slice(0, 6).map((img, i) => (
        <a
          key={i}
          href={img.link}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'group relative overflow-hidden rounded-2xl shadow-lg transition-all duration-300',
            'hover:shadow-2xl hover:scale-[1.02]',
            i === 0 ? 'col-span-2 aspect-[16/9]' : 'aspect-[4/3]'
          )}
        >
          <img
            src={img.original || img.thumbnail}
            alt={img.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
            onError={(e) => { (e.target as HTMLImageElement).src = img.thumbnail }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <span className="absolute bottom-2 left-2 right-2 text-white text-[10px] font-bold truncate opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow">
            {img.title}
          </span>
        </a>
      ))}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════
// SPECIALTY DETAIL PANEL (visual)
// ═══════════════════════════════════════════════════════════════════
function SpecialtyDetail({ specialty, lang, onClose }: { specialty: DivingSpecialty; lang: string; onClose: () => void }) {
  const { t } = useTranslation()
  const { images, loading } = useSpecialtyImages(specialty, lang)
  const name = lang === 'es' ? specialty.name : specialty.nameEn
  const desc = lang === 'es' ? specialty.description : specialty.descriptionEn
  const quizCount = getSpecialtyCards(specialty.id).length

  return (
    <div className="rounded-3xl border-2 border-white/40 shadow-2xl overflow-hidden animate-correct-pop bg-white/95 backdrop-blur-sm">
      {/* Hero image header */}
      <div className="relative h-48 sm:h-56 overflow-hidden">
        {images[0] ? (
          <img src={images[0].original || images[0].thumbnail} alt={name} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).src = images[0].thumbnail }} />
        ) : (
          <div className={cn('w-full h-full', `bg-gradient-to-br ${specialty.gradient}`, loading && 'animate-pulse')} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/40 backdrop-blur-md hover:bg-black/60 flex items-center justify-center text-white font-bold transition-all border border-white/20">✕</button>
        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex items-center gap-3">
            <span className="text-4xl drop-shadow-xl">{specialty.emoji}</span>
            <div>
              <h2 className="font-black text-2xl text-white drop-shadow-lg">{name}</h2>
              <p className="text-white/85 text-sm font-medium drop-shadow">{desc}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <DifficultyBadge level={specialty.difficulty} t={t} />
            <PrereqBadge prereq={specialty.prerequisite} t={t} />
            {specialty.requiredDives > 0 && (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-white/20 text-white backdrop-blur-sm">{specialty.requiredDives} {t('divingAcademy.dives')}</span>
            )}
            {specialty.maxDepth && (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-white/20 text-white backdrop-blur-sm">↓ {specialty.maxDepth}m</span>
            )}
          </div>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {/* Highlights */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {specialty.highlights.map((h, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-gray-50 to-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <span className="text-2xl">{h.emoji}</span>
              <span className="text-sm font-medium text-gray-700">{lang === 'es' ? h.text : h.textEn}</span>
            </div>
          ))}
        </div>

        {/* Quiz CTA */}
        {quizCount > 0 && (
          <div className="flex items-center justify-between p-4 rounded-2xl bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="text-3xl">🧠</span>
              <div>
                <p className="font-bold text-gray-900">{t('divingAcademy.quiz')}</p>
                <p className="text-sm text-gray-600">{quizCount} {t('divingAcademy.questions')}</p>
              </div>
            </div>
            <Button as="link" to={`/quizmoji?specialty=${specialty.id}`} variant="primarySolid" size="sm">
              {t('divingAcademy.practiceQuiz')}
            </Button>
          </div>
        )}

        {/* Photo gallery */}
        <div>
          <h3 className="font-black text-gray-900 mb-3 flex items-center gap-2">📸 {t('divingAcademy.gallery')}</h3>
          <ImageGallery images={images.slice(1)} loading={loading} />
          {images.length > 0 && (
            <p className="text-[10px] text-gray-400 mt-2 text-center">{t('divingAcademy.photoCredit')}</p>
          )}
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5">
          {specialty.tags.map(tag => (
            <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 font-medium">#{tag}</span>
          ))}
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════
// FEATURED GALLERY
// ═══════════════════════════════════════════════════════════════════
function FeaturedGallery({ lang }: { lang: string }) {
  const { t } = useTranslation()
  const { images, loading } = useFeaturedGallery(lang)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-black text-white text-xl drop-shadow-lg flex items-center gap-2">
          📸 {t('divingAcademy.featuredGallery')}
        </h2>
      </div>
      <p className="text-white/70 text-sm font-medium -mt-1">{t('divingAcademy.featuredGalleryDesc')}</p>

      {loading ? (
        <div className="grid grid-cols-3 gap-2">
          {[...Array(6)].map((_, i) => <ImageShimmer key={i} className="aspect-square" />)}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {images.slice(0, 9).map((img, i) => (
            <a
              key={i}
              href={img.link}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'group relative overflow-hidden rounded-2xl shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:z-10',
                i === 0 ? 'col-span-2 row-span-2 aspect-square' : 'aspect-square'
              )}
            >
              <img
                src={img.original || img.thumbnail}
                alt={img.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
                onError={(e) => { (e.target as HTMLImageElement).src = img.thumbnail }}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300" />
              <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-white text-[9px] font-bold truncate">{img.title}</p>
                <p className="text-white/60 text-[8px] truncate">{img.source}</p>
              </div>
            </a>
          ))}
        </div>
      )}
      <p className="text-[10px] text-white/40 text-center">{t('divingAcademy.photoCredit')}</p>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════
// DIVE SPOTS (Google Maps)
// ═══════════════════════════════════════════════════════════════════
function DiveSpots() {
  const { t } = useTranslation()
  const { spots, loading } = useDiveSpots()

  return (
    <div className="space-y-3">
      <h2 className="font-black text-white text-xl drop-shadow-lg flex items-center gap-2">
        📍 {t('divingAcademy.diveSpots')}
      </h2>
      <p className="text-white/70 text-sm font-medium -mt-1">{t('divingAcademy.diveSpotsDesc')}</p>

      {loading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 rounded-2xl bg-white/10 animate-pulse" />
          ))}
        </div>
      ) : spots.length === 0 ? (
        <p className="text-white/50 text-sm text-center py-6">{t('divingAcademy.noSpots')}</p>
      ) : (
        <div className="space-y-2">
          {spots.map((spot, i) => (
            <div
              key={i}
              className="flex items-start gap-3 p-4 rounded-2xl bg-white/90 backdrop-blur-sm border border-white/40 shadow-lg hover:shadow-xl hover:scale-[1.01] transition-all"
            >
              {/* Thumbnail or map icon */}
              <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0 bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center shadow-md">
                {spot.thumbnail ? (
                  <img src={spot.thumbnail} alt={spot.title} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <span className="text-2xl">📍</span>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-900 text-sm truncate">{spot.title}</h3>
                {spot.address && (
                  <p className="text-xs text-gray-500 truncate mt-0.5">📍 {spot.address}</p>
                )}
                <div className="flex items-center gap-3 mt-1.5">
                  {spot.rating && (
                    <span className="text-xs font-bold text-amber-600 flex items-center gap-1">
                      ⭐ {spot.rating}
                    </span>
                  )}
                  {spot.reviews && (
                    <span className="text-xs text-gray-500">
                      ({spot.reviews} {t('divingAcademy.reviews')})
                    </span>
                  )}
                  {spot.type && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 font-bold">
                      {spot.type}
                    </span>
                  )}
                </div>
              </div>

              {/* Arrow */}
              <span className="text-gray-300 text-lg shrink-0 mt-3">›</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════
// DIVING NEWS (Google News)
// ═══════════════════════════════════════════════════════════════════
function DivingNews({ lang }: { lang: string }) {
  const { t } = useTranslation()
  const { news, loading } = useDivingNews(lang)

  return (
    <div className="space-y-3">
      <h2 className="font-black text-white text-xl drop-shadow-lg flex items-center gap-2">
        📰 {t('divingAcademy.divingNews')}
      </h2>
      <p className="text-white/70 text-sm font-medium -mt-1">{t('divingAcademy.divingNewsDesc')}</p>

      {loading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-20 rounded-2xl bg-white/10 animate-pulse" />
          ))}
        </div>
      ) : news.length === 0 ? (
        <p className="text-white/50 text-sm text-center py-6">{t('divingAcademy.noNews')}</p>
      ) : (
        <div className="space-y-2">
          {news.map((item, i) => (
            <a
              key={i}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 p-4 rounded-2xl bg-white/90 backdrop-blur-sm border border-white/40 shadow-lg hover:shadow-xl hover:scale-[1.01] transition-all group"
            >
              {/* Thumbnail */}
              <div className="w-20 h-16 rounded-xl overflow-hidden shrink-0 bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center shadow-md">
                {item.thumbnail ? (
                  <img src={item.thumbnail} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <span className="text-2xl">📰</span>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-900 text-sm line-clamp-2 group-hover:text-indigo-600 transition-colors">
                  {item.title}
                </h3>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-bold">
                    {item.source}
                  </span>
                  {item.date && (
                    <span className="text-[10px] text-gray-400">{item.date}</span>
                  )}
                </div>
                {item.snippet && (
                  <p className="text-[11px] text-gray-500 mt-1 line-clamp-1">{item.snippet}</p>
                )}
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════
// LEARNING PATH (visual with images)
// ═══════════════════════════════════════════════════════════════════
function LearningPath({ lang, thumbs }: { lang: string; thumbs: Record<string, string> }) {
  const { t } = useTranslation()
  const steps = getDivingLearningPath()

  return (
    <div className="space-y-3">
      <h2 className="font-black text-white text-xl drop-shadow-lg flex items-center gap-2">
        🗺️ {t('divingAcademy.learningPath')}
      </h2>
      <div className="relative">
        <div className="absolute left-7 top-8 bottom-8 w-1 bg-white/20 rounded-full" />

        {steps.map((step, i) => {
          const thumb = thumbs[step.id]
          return (
            <div key={step.id} className="relative flex items-start gap-4 mb-3">
              {/* Node with image */}
              <div className={cn(
                'w-14 h-14 rounded-2xl overflow-hidden shrink-0 z-10 shadow-xl border-2 border-white/50',
              )}>
                {thumb ? (
                  <img src={thumb} alt={step.name} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className={cn('w-full h-full flex items-center justify-center text-2xl', `bg-gradient-to-br ${step.gradient}`)}>
                    {step.emoji}
                  </div>
                )}
              </div>

              {/* Content card */}
              <div className="flex-1 p-4 rounded-2xl bg-white/90 backdrop-blur-sm border border-white/40 shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center gap-2">
                  <span className={cn(
                    'text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider',
                    step.type === 'course' ? 'bg-cyan-100 text-cyan-700' :
                    step.type === 'certification' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'
                  )}>
                    {step.type === 'course' ? t('divingAcademy.courses') : step.type === 'certification' ? t('divingAcademy.level') : t('divingAcademy.specialties')}
                  </span>
                  <span className="text-gray-400 text-xs font-bold">{i + 1}/{steps.length}</span>
                </div>
                <h3 className="font-black text-gray-900 mt-1 flex items-center gap-1.5">
                  <span>{step.emoji}</span> {lang === 'es' ? step.name : step.nameEn}
                </h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  {lang === 'es' ? step.description : step.descriptionEn}
                </p>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════
// CERTIFICATION LEVELS (with images)
// ═══════════════════════════════════════════════════════════════════
function CertificationLevels({ lang, certImages }: { lang: string; certImages: Record<string, string> }) {
  const { t } = useTranslation()

  return (
    <div className="space-y-3">
      <h2 className="font-black text-white text-xl drop-shadow-lg flex items-center gap-2">
        🏅 {t('divingAcademy.certifications')}
      </h2>
      <div className="grid gap-3">
        {certificationLevels.map((level) => {
          const bgImg = certImages[level.id]
          return (
            <div
              key={level.id}
              className="rounded-2xl border-2 border-white/40 shadow-xl overflow-hidden relative h-36"
            >
              {/* Background image */}
              {bgImg ? (
                <img src={bgImg} alt={level.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
              ) : (
                <div className={cn('absolute inset-0', `bg-gradient-to-br ${level.gradient}`)} />
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />

              {/* Content */}
              <div className="relative z-10 h-full flex flex-col justify-end p-4">
                <div className="flex items-center gap-3">
                  <span className="text-4xl drop-shadow-xl">{level.emoji}</span>
                  <div>
                    <h3 className="font-black text-white text-lg drop-shadow-lg">
                      {lang === 'es' ? level.name : level.nameEn}
                    </h3>
                    <p className="text-white/80 text-xs mt-0.5 line-clamp-1">
                      {lang === 'es' ? level.description : level.descriptionEn}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-white/20 text-white backdrop-blur-sm">
                    ↓ {level.maxDepth}m
                  </span>
                  <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-white/20 text-white backdrop-blur-sm">
                    🤿 {level.minDives}+ {t('divingAcademy.dives')}
                  </span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════
export default function DivingAcademy() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language?.startsWith('en') ? 'en' : 'es'

  const [activeTab, setActiveTab] = useState<'specialties' | 'explore' | 'certifications'>('specialties')
  const [selectedSpecialty, setSelectedSpecialty] = useState<DivingSpecialty | null>(null)
  const [activeTag, setActiveTag] = useState<string>('all')

  const allTags = getAllTags()
  const thumbs = useSpecialtyThumbnails(lang)
  const certImages = useCertImages(lang)

  const filteredSpecialties = activeTag === 'all'
    ? divingSpecialties
    : divingSpecialties.filter(s => s.tags.includes(activeTag))

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-indigo-600 via-blue-600 to-cyan-600">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_0%,rgba(255,255,255,0.15)_0%,transparent_50%)]" />
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-blue-800/30 to-transparent" />
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white/5 bubble-float"
            style={{
              width: `${6 + (i % 6) * 5}px`, height: `${6 + (i % 6) * 5}px`,
              left: `${(i * 5) % 100}%`, top: `${5 + (i * 5) % 80}%`,
              animationDelay: `${i * 0.2}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-4 py-4 pb-28">
        {/* Back */}
        <Link to="/mojiland" className="inline-flex items-center gap-1 text-white/70 hover:text-white font-bold text-sm mb-3 transition-colors">
          {t('divingAcademy.backToAcademy')}
        </Link>

        {/* Hero Banner with rotating images */}
        <HeroBanner lang={lang} />

        {/* Stats */}
        <div className="mb-5">
          <StatsCard />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-5 overflow-x-auto pb-1 scrollbar-hide">
          {[
            { id: 'specialties' as const, emoji: '🏅', label: t('divingAcademy.specialties') },
            { id: 'explore' as const, emoji: '🌊', label: t('divingAcademy.discoverMore') },
            { id: 'certifications' as const, emoji: '📜', label: t('divingAcademy.certifications') },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSelectedSpecialty(null) }}
              className={cn(
                'flex items-center gap-2 px-5 py-2.5 rounded-2xl font-bold text-sm whitespace-nowrap transition-all duration-200',
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-xl scale-105'
                  : 'bg-white/15 text-white hover:bg-white/25'
              )}
            >
              <span>{tab.emoji}</span> {tab.label}
            </button>
          ))}
        </div>

        {/* ═══════════════ TAB: SPECIALTIES ═══════════════ */}
        {activeTab === 'specialties' && (
          <>
            {selectedSpecialty && (
              <div className="mb-5">
                <SpecialtyDetail specialty={selectedSpecialty} lang={lang} onClose={() => setSelectedSpecialty(null)} />
              </div>
            )}

            {/* Tag filters */}
            <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1.5 scrollbar-hide">
              <button
                onClick={() => setActiveTag('all')}
                className={cn('px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all', activeTag === 'all' ? 'bg-white text-gray-900 shadow-lg' : 'bg-white/15 text-white hover:bg-white/25')}
              >
                {t('divingAcademy.all')} ({divingSpecialties.length})
              </button>
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => setActiveTag(tag)}
                  className={cn('px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all', activeTag === tag ? 'bg-white text-gray-900 shadow-lg' : 'bg-white/15 text-white hover:bg-white/25')}
                >
                  #{tag} ({divingSpecialties.filter(s => s.tags.includes(tag)).length})
                </button>
              ))}
            </div>

            {/* Specialty cards grid — 2 cols on wider screens */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filteredSpecialties.map(spec => (
                <SpecialtyCard
                  key={spec.id}
                  specialty={spec}
                  lang={lang}
                  isSelected={selectedSpecialty?.id === spec.id}
                  onSelect={() => setSelectedSpecialty(selectedSpecialty?.id === spec.id ? null : spec)}
                  thumbnail={thumbs[spec.id]}
                />
              ))}
            </div>
          </>
        )}

        {/* ═══════════════ TAB: EXPLORE (Gallery + Spots + News) ═══════════════ */}
        {activeTab === 'explore' && (
          <div className="space-y-8">
            <FeaturedGallery lang={lang} />
            <DiveSpots />
            <DivingNews lang={lang} />
            <LearningPath lang={lang} thumbs={thumbs} />
          </div>
        )}

        {/* ═══════════════ TAB: CERTIFICATIONS ═══════════════ */}
        {activeTab === 'certifications' && (
          <CertificationLevels lang={lang} certImages={certImages} />
        )}

        {/* Navigation */}
        <div className="flex justify-center gap-3 mt-8">
          <Button as="link" to="/mojiland" variant="outline" size="md">🏝️ Mojiland</Button>
          <Button as="link" to="/" variant="outline" size="md">🎮 {t('nav.games')}</Button>
        </div>
      </div>
    </div>
  )
}
